/**
 * ConceptSelector Component
 * Reusable autocomplete component for selecting concept values
 */

import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";

interface ConceptSelectorProps {
  label: string;
  options: string[];
  value: string[];
  loading?: boolean;
  maxSelections?: number;
  totalMaxSelections?: number;
  otherSelectionsCount?: number;
  tooltipText?: string;
  onInputChange?: (value: string) => void;
  onChange: (value: string[]) => void;
}

export const ConceptSelector: React.FC<ConceptSelectorProps> = ({
  label,
  options,
  value,
  loading = false,
  maxSelections = 5,
  totalMaxSelections = 20,
  otherSelectionsCount = 0,
  tooltipText,
  onInputChange,
  onChange,
}) => {
  const handleInputChange = (
    _event: React.SyntheticEvent,
    inputValue: string,
    reason: string
  ) => {
    if (reason === "input" && onInputChange) {
      onInputChange(inputValue);
    }
  };

  // When dropdown opens, trigger initial load if no options available
  const handleOpen = () => {
    if (options.length === 0 && !loading && onInputChange) {
      console.log(
        `[ConceptSelector] ${label} - Dropdown opened, loading initial values`
      );
      onInputChange("");
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    // Limit to maxSelections per dropdown
    if (newValue.length > maxSelections) {
      return;
    }

    // Check total across all dropdowns
    if (otherSelectionsCount + newValue.length > totalMaxSelections) {
      return;
    }

    onChange(newValue);
  };

  // Ensure selected values are always in options list
  // This prevents MUI Autocomplete from losing selected values when options change
  const allOptions = React.useMemo(() => {
    const optionsSet = new Set(options);
    const combined = [...options];

    // Add any selected values that aren't in current options
    value.forEach((val) => {
      if (!optionsSet.has(val)) {
        combined.push(val);
      }
    });

    return combined;
  }, [options, value]);

  return (
    <Box>
      {/* Label with icon above the autocomplete */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <Box
          component="label"
          sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}
        >
          {label}
        </Box>
        {tooltipText && (
          <Tooltip title={tooltipText} arrow placement="top">
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: "text.secondary",
                cursor: "help",
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Autocomplete component */}
      <Autocomplete
        multiple
        options={allOptions}
        value={value}
        loading={loading}
        onOpen={handleOpen}
        onInputChange={handleInputChange}
        onChange={handleChange}
        getOptionDisabled={(option) => {
          const isSelected = value.includes(option);
          if (isSelected) return false;

          const isAtDropdownLimit = value.length >= maxSelections;
          const isAtTotalLimit =
            otherSelectionsCount + value.length >= totalMaxSelections;

          return isAtDropdownLimit || isAtTotalLimit;
        }}
        filterSelectedOptions={false}
        disablePortal
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "flip",
                enabled: true,
                options: {
                  altBoundary: true,
                  rootBoundary: "viewport",
                  padding: 8,
                },
              },
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  altAxis: true,
                  altBoundary: true,
                  tether: true,
                  rootBoundary: "viewport",
                  padding: 8,
                },
              },
            ],
          },
          listbox: {
            style: { maxHeight: "200px" },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`${value.length}/${maxSelections} selected`}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return (
              <Chip key={key} label={option} {...chipProps} size="small" />
            );
          })
        }
      />
    </Box>
  );
};
